import { useState } from "react";
import FaqGradient from "../assets/faq-gradient.png";
import SeparatorTop from "../assets/separator-top.png";

type FaqEntry = {
    q: string;
    a: React.ReactNode;
};

const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const entries: FaqEntry[] = [
    {
        q: "What is my tenant ID and where do I find it?",
        a: (
            <>
                <p>
                    Your Microsoft 365 tenant ID is a globally unique identifier (GUID) assigned to your organization
                    when it was created in Microsoft Entra ID (formerly Azure AD). It identifies your specific tenant
                    across every Microsoft cloud service — Exchange Online, SharePoint, Teams, Intune, and more.
                </p>
                <p>
                    The fastest way to find it: sign in to the{" "}
                    <a href="https://entra.microsoft.com" target="_blank" rel="noreferrer">Entra admin center</a>,
                    go to Overview, and copy the Tenant ID shown under Tenant information. You can also retrieve it
                    from PowerShell with <code>Get-MgOrganization</code>.
                </p>
            </>
        ),
    },
    {
        q: "Why it matters when doing a Microsoft 365 tenant-to-tenant migration",
        a: (
            <p>
                In a tenant-to-tenant migration, you’re moving identities, mailboxes, files, Teams, sites, and group
                memberships between two distinct Microsoft 365 environments. Every environment is identified by its
                tenant ID — and almost every cross-tenant operation, from PowerShell scripts to third-party tooling,
                needs both source and destination IDs to map permissions correctly. Verifying the right tenant IDs
                up front prevents misrouted data and broken access on cutover day.
            </p>
        ),
    },
    {
        q: "The details: every place tenant ID shows up",
        a: (
            <>
                <p>Tenant ID surfaces in a few different places across the Microsoft cloud. Here’s a quick reference.</p>
                <table className="faq-table">
                    <thead>
                        <tr>
                            <th>Where</th>
                            <th>What you’ll see</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Entra admin center → Overview</td>
                            <td>Listed as “Tenant ID” — a GUID like <code>aaaaaaaa-bbbb-…</code></td>
                        </tr>
                        <tr>
                            <td>Microsoft 365 admin center</td>
                            <td>Under Settings → Org settings → Organization profile</td>
                        </tr>
                        <tr>
                            <td>Azure portal → Microsoft Entra ID</td>
                            <td>Properties panel shows the Tenant ID</td>
                        </tr>
                        <tr>
                            <td>PowerShell (Microsoft Graph)</td>
                            <td><code>Get-MgOrganization</code> returns the tenant ID and display name</td>
                        </tr>
                        <tr>
                            <td>PowerShell (Az module)</td>
                            <td><code>Get-AzTenant</code> returns ID and domain</td>
                        </tr>
                        <tr>
                            <td>Login URL / well-known OIDC</td>
                            <td>Embedded in Azure URLs and at <code>/openid-configuration</code></td>
                        </tr>
                        <tr>
                            <td>App registration manifest</td>
                            <td>Embedded in issuer URLs across token claims</td>
                        </tr>
                    </tbody>
                </table>
            </>
        ),
    },
    {
        q: "Do I need a tenant ID to set up ShareGate?",
        a: (
            <p>
                For most ShareGate workflows, no — you sign in with your Microsoft 365 admin account and ShareGate
                resolves the tenant context automatically. For tenant-to-tenant migration, you’ll be asked to grant
                consent in both source and destination tenants, where the tenant ID is used behind the scenes. You
                only need the GUID on hand for advanced scenarios: scripting, multi-tenant configuration, or when
                raising a support ticket so we can identify your environment quickly.
            </p>
        ),
    },
    {
        q: "What is the difference between my MS tenant ID and my ShareGate workspace ID?",
        a: (
            <p>
                Your Microsoft tenant ID is issued by Microsoft and identifies your Entra ID tenant across the
                Microsoft cloud. Your ShareGate workspace ID is issued by ShareGate and identifies your team’s
                workspace inside the ShareGate product. They are two separate identifiers for two separate systems:
                a single ShareGate workspace can connect to one or more Microsoft tenants depending on your
                migration scenario.
            </p>
        ),
    },
];

const FaqItem = ({ entry, index, openIndex, setOpenIndex }: {
    entry: FaqEntry;
    index: number;
    openIndex: number;
    setOpenIndex: (i: number) => void;
}) => {
    const open = openIndex === index;
    return (
        <div className={`faq-item ${open ? "faq-item--open" : ""}`}>
            <button
                className="faq-item__btn"
                type="button"
                aria-expanded={open}
                onClick={() => setOpenIndex(open ? -1 : index)}
            >
                <span>{entry.q}</span>
                <span className="faq-item__icon"><ChevronIcon /></span>
            </button>
            {open && <div className="faq-item__body">{entry.a}</div>}
        </div>
    );
};

type FAQProps = { children?: React.ReactNode };

export const FAQ = ({ children }: FAQProps) => {
    const [openIndex, setOpenIndex] = useState(0);
    return (
        <section className="faq-section">
            <img className="faq-section__bg" src={FaqGradient} alt="" aria-hidden="true" />
            <img className="faq-section__separator faq-section__separator--top" src={SeparatorTop} alt="" aria-hidden="true" />
            <div className="faq-container">
                <h2 className="faq-title">Frequently asked questions</h2>
                {entries.map((entry, i) => (
                    <FaqItem
                        key={i}
                        entry={entry}
                        index={i}
                        openIndex={openIndex}
                        setOpenIndex={setOpenIndex}
                    />
                ))}
            </div>
            {children && <div className="faq-section__cta">{children}</div>}
            <img className="faq-section__separator faq-section__separator--bottom" src={SeparatorTop} alt="" aria-hidden="true" />
        </section>
    );
};
